import React, { useEffect, useMemo, useState } from 'react';
import { X, CheckCircle2, Lock, Hand, History, Loader2, RotateCcw, AlertTriangle } from 'lucide-react';
import { safeFetch } from '../../../lib/api';
import { notifyToast } from '../../../lib/toastStore';
import type { BulkPreview, BulkConfirmResult, BulkPreviewItem } from './types';

interface BulkAdvanceModalProps {
  uids: string[];
  onClose: () => void;
  onDone: () => void;
}

const CATEGORY_META: Record<BulkPreviewItem['category'], { label: string; icon: React.ReactNode; tone: string }> = {
  eligible: {
    label: 'Eligible',
    icon: <CheckCircle2 className="h-4 w-4 text-emerald-600" />,
    tone: 'border-emerald-200 bg-emerald-50/60',
  },
  blocked: {
    label: 'Blocked',
    icon: <Lock className="h-4 w-4 text-amber-600" />,
    tone: 'border-amber-200 bg-amber-50/60',
  },
  manual: {
    label: 'Manual decision required',
    icon: <Hand className="h-4 w-4 text-sky-600" />,
    tone: 'border-sky-200 bg-sky-50/60',
  },
  already_processed: {
    label: 'Already processed',
    icon: <History className="h-4 w-4 text-slate-500" />,
    tone: 'border-slate-200 bg-slate-50',
  },
  not_found: {
    label: 'Not found',
    icon: <AlertTriangle className="h-4 w-4 text-red-600" />,
    tone: 'border-red-200 bg-red-50/60',
  },
};

export const BulkAdvanceModal: React.FC<BulkAdvanceModalProps> = ({ uids, onClose, onDone }) => {
  const [preview, setPreview] = useState<BulkPreview | null>(null);
  const [result, setResult] = useState<BulkConfirmResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [failedOnly, setFailedOnly] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set(['eligible']));

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await safeFetch('/api/admin/workflow/bulk/advance', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ uids, preview: true }),
        });
        setPreview(data as BulkPreview);
      } catch (err: any) {
        notifyToast('error', err.message || 'Failed to preview bulk advance');
        onClose();
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const groups = useMemo(() => {
    const map = new Map<string, BulkPreviewItem[]>();
    (preview?.items || []).forEach((it) => {
      const arr = map.get(it.category) || [];
      arr.push(it);
      map.set(it.category, arr);
    });
    return map;
  }, [preview]);

  const toggleGroup = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const handleConfirm = async (onlyFailed: boolean = false) => {
    try {
      setConfirming(true);
      setFailedOnly(onlyFailed);
      const targetUids = onlyFailed && result
        ? result.results.filter((r) => !r.ok || !r.advanced).map((r) => r.uid)
        : uids;
      const data = await safeFetch('/api/admin/workflow/bulk/advance', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uids: targetUids, confirm: true }),
      });
      setResult(data as BulkConfirmResult);
      notifyToast(
        'success',
        `${(data as BulkConfirmResult).summary.advanced} advanced, ${(data as BulkConfirmResult).summary.skipped} skipped`
      );
      onDone();
    } catch (err: any) {
      notifyToast('error', err.message || 'Bulk advance failed');
    } finally {
      setConfirming(false);
    }
  };

  const failedCount = result ? result.results.filter((r) => !r.ok || !r.advanced).length : 0;
  const eligibleCount = preview?.summary.eligible || 0;

  const renderGroup = (category: BulkPreviewItem['category'], title: string, hint: string) => {
    const items = groups.get(category) || [];
    if (!items.length) return null;
    const meta = CATEGORY_META[category];
    const open = expanded.has(category);
    return (
      <div key={category} className={`border rounded-xl ${meta.tone}`}>
        <button
          type="button"
          onClick={() => toggleGroup(category)}
          aria-expanded={open}
          className="w-full flex items-center gap-2 px-3 py-2.5 text-left cursor-pointer"
        >
          {meta.icon}
          <span className="text-xs font-bold text-slate-800">
            {title} ({items.length})
          </span>
          <span className="text-[11px] text-slate-500 ml-auto">{open ? 'Hide' : 'Show'}</span>
        </button>
        {open && (
          <div className="px-3 pb-3 space-y-1.5">
            <p className="text-[11px] text-slate-500">{hint}</p>
            {items.map((it) => (
              <div key={it.uid} className="bg-white border border-slate-200/80 rounded-lg px-2.5 py-1.5 text-xs">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-slate-800 truncate">{it.displayName}</span>
                  <span className="font-mono text-[11px] text-slate-500 shrink-0">#{it.applicationId}</span>
                </div>
                {category === 'eligible' && it.from && it.to && (
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    {it.from} → {it.to}
                  </p>
                )}
                {it.reason && <p className="text-[11px] text-slate-500 mt-0.5">{it.reason}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 z-[9200] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-[2px]"
      role="dialog"
      aria-modal="true"
      aria-label="Bulk advance applicants"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col border border-slate-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-base font-extrabold text-slate-900 tracking-tight">Bulk advance</h2>
            <p className="text-xs text-slate-500 font-medium">
              {uids.length} applicant{uids.length === 1 ? '' : 's'} selected
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close bulk advance dialog"
            className="p-2 rounded-xl hover:bg-slate-100 text-slate-500 cursor-pointer min-w-[44px] min-h-[44px] flex items-center justify-center"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-4 overflow-y-auto space-y-3 flex-1">
          {loading ? (
            <div className="space-y-2 py-6">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-12 bg-slate-100 rounded-xl animate-pulse" />
              ))}
              <p className="text-xs text-slate-500 text-center font-medium">Checking eligibility with the workflow engine…</p>
            </div>
          ) : result ? (
            <>
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-center gap-3">
                <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
                <p className="text-sm font-bold text-emerald-800">
                  {result.summary.advanced} advanced{failedOnly ? ' on retry' : ''}
                  {', '}
                  {result.summary.skipped} skipped
                </p>
                <span className="text-[11px] text-emerald-700 ml-auto font-mono">run {result.runId}</span>
              </div>
              <div className="space-y-1.5">
                {result.results.map((r) => (
                  <div
                    key={r.uid}
                    className={`border rounded-xl px-3 py-2 text-xs flex items-start gap-2 ${
                      r.ok && r.advanced ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200 bg-red-50/50'
                    }`}
                  >
                    {r.ok && r.advanced ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
                    )}
                    <div className="min-w-0">
                      <span className="font-semibold text-slate-800">{r.displayName}</span>
                      {r.from && r.to && r.advanced && (
                        <span className="text-slate-500"> · {r.from} → {r.to}</span>
                      )}
                      {r.reason && <p className="text-slate-500 mt-0.5">{r.reason}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : preview ? (
            <>
              <div className="bg-sky-50 border border-sky-200 rounded-xl px-3.5 py-2.5 text-[11px] text-sky-800 font-medium">
                Manual-decision transitions (submitted → ready for screening, and SC/SP closures) are
                <span className="font-bold"> never bulk-advanced</span> — they require an individual admin decision.
              </div>
              {renderGroup('eligible', 'Eligible', 'These will move to the next stage on confirm.')}
              {renderGroup('blocked', 'Blocked', 'The workflow engine says these cannot advance yet. Reasons are from the server, not computed here.')}
              {renderGroup('manual', 'Manual decision required', 'Excluded from bulk advance — each needs an individual admin decision.')}
              {renderGroup('already_processed', 'Already processed', 'Already at the target stage when the preview was generated.')}
              {renderGroup('not_found', 'Not found', 'These uids no longer resolve to an applicant record.')}
              {preview.items.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-6 font-medium">No applicants to preview.</p>
              )}
            </>
          ) : null}
        </div>

        <div className="px-5 py-4 border-t border-slate-100 flex items-center gap-2 justify-end">
          {result ? (
            <>
              {failedCount > 0 && (
                <button
                  type="button"
                  onClick={() => handleConfirm(true)}
                  disabled={confirming}
                  className="px-4 py-2.5 min-h-[44px] rounded-xl bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white text-xs font-bold flex items-center gap-1.5 cursor-pointer"
                >
                  {confirming ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                  Retry failed ({failedCount})
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 min-h-[44px] rounded-xl bg-brand hover:bg-brand-active text-white text-xs font-bold cursor-pointer"
              >
                Done
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 min-h-[44px] rounded-xl border border-slate-200 text-slate-700 text-xs font-bold hover:bg-slate-50 cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleConfirm(false)}
                disabled={confirming || eligibleCount === 0 || loading}
                title={eligibleCount === 0 ? 'No eligible applicants' : `Advance ${eligibleCount} applicant${eligibleCount === 1 ? '' : 's'}`}
                className="px-4 py-2.5 min-h-[44px] rounded-xl bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] disabled:opacity-50 text-white text-xs font-bold flex items-center gap-1.5 cursor-pointer"
              >
                {confirming ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Confirm advance ({eligibleCount})
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
